import React from 'react';
import { DocumentLineItemWorkOrder } from '../types/Types';

interface UseNestedDocumentSelectionParams {
  localRows: DocumentLineItemWorkOrder[];
  deleteDocumentLineItemWorkOrder: (id: string) => Promise<void>;
  refetch: () => Promise<void> | void;
}

/**
 * Row selection + bulk-delete confirmation flow for NestedDocumentTable.
 * Extracted 1:1 from NestedDocumentTable.tsx (CLAUDE.md rule 9 split) — no behaviour change.
 */
export function useNestedDocumentSelection({
  localRows,
  deleteDocumentLineItemWorkOrder,
  refetch,
}: UseNestedDocumentSelectionParams) {
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [deletingRows, setDeletingRows] = React.useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  // Handle row selection
  const handleRowSelect = (rowId: string) => {
    setSelectedRows(prev =>
      prev.includes(rowId)
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  // Handle select all
  const handleSelectAll = () => {
    const allRowIds = localRows.filter(row => row._id).map(row => row._id!);
    if (selectedRows.length === allRowIds.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allRowIds);
    }
  };

  // Delete selected rows
  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;
    setShowDeleteModal(true);
  };

  // Confirm delete selected rows
  const handleConfirmDelete = async () => {
    if (selectedRows.length === 0) return;

    try {
      // Delete all selected rows
      for (const rowId of selectedRows) {
        setDeletingRows(prev => new Set(prev).add(rowId));
        try {
          await deleteDocumentLineItemWorkOrder(rowId);
        } catch (err) {
          // error is handled by hook
        } finally {
          setDeletingRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(rowId);
            return newSet;
          });
        }
      }
      // Clear selections and close modal
      setSelectedRows([]);
      setShowDeleteModal(false);

      // Refetch data from backend to get updated valuation prices
      await refetch();
    } catch (err) {
      // error is handled by hook
    }
  };

  return {
    selectedRows,
    deletingRows,
    showDeleteModal,
    setShowDeleteModal,
    handleRowSelect,
    handleSelectAll,
    handleDeleteSelected,
    handleConfirmDelete,
  };
}

export default useNestedDocumentSelection;
